import { useState } from "react";
import useSWR from "swr";

import { request } from "@/api/client";
import { useToast } from "@/components/ToastProvider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fetcher } from "@/lib/fetcher";

type AutopilotState = {
  rules_loaded?: number;
  workflow_count?: number;
  token_recent?: number;
  recent_triggers?: Array<{
    id?: string;
    summary?: string;
    timestamp?: string;
  }>;
};

type AutopilotPanelProps = {
  canManage: boolean;
};

export function AutopilotPanel({ canManage }: AutopilotPanelProps) {
  const { data, error, isLoading, mutate } = useSWR<AutopilotState>("/autopilot/state", fetcher, {
    refreshInterval: 15000,
  });
  const { push } = useToast();
  const [running, setRunning] = useState(false);

  const handleRun = async () => {
    setRunning(true);
    try {
      await request("/autopilot/run", { method: "POST" });
      push({ title: "Autopilot", message: "Manual autopilot pass dispatched." });
      await mutate();
    } catch (err) {
      push({
        title: "Autopilot error",
        message: err instanceof Error ? err.message : "Unable to run autopilot.",
      });
    } finally {
      setRunning(false);
    }
  };

  const stats = [
    { label: "Rules loaded", value: data?.rules_loaded ?? 0 },
    { label: "Workflow count", value: data?.workflow_count ?? 0 },
    { label: "Recent tokens", value: data?.token_recent ?? 0 },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle>Autopilot control</CardTitle>
          <p className="text-sm text-muted-foreground">
            Monitor streamlined runs and inspect the most recent dispatches from the autopilot engine.
          </p>
        </div>
        {canManage && (
          <Button type="button" variant="outline" onClick={() => void handleRun()} disabled={running}>
            {running ? "Running…" : "Run pass"}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <p className="text-sm text-rose-400">Unable to load autopilot state.</p>}
        <div className="grid gap-3 sm:grid-cols-3">
          {stats.map((stat) => (
            <div key={stat.label} className="rounded-xl border border-border/80 bg-background/60 p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{stat.label}</p>
              <p className="text-2xl font-semibold text-foreground">{isLoading ? "…" : stat.value}</p>
            </div>
          ))}
        </div>
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">Recent triggers</p>
            <Badge variant="outline">{(data?.recent_triggers?.length ?? 0).toString()}</Badge>
          </div>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading events…</p>
          ) : !data?.recent_triggers?.length ? (
            <p className="text-sm text-muted-foreground">No recent triggers recorded.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.recent_triggers.slice(0, 10).map((trigger, index) => (
                <li key={trigger.id ?? index} className="rounded-lg border border-border/70 bg-background/40 p-3">
                  <p className="font-medium text-foreground">{trigger.summary ?? "Trigger dispatched"}</p>
                  <p className="text-xs text-muted-foreground">
                    {trigger.timestamp ? new Date(trigger.timestamp).toLocaleString() : "Unknown time"}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
